import React, { useState, createElement } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Phone, CalendarDays, MessageSquare, Shield, ArrowRight, PhoneMissed, PhoneIncoming, CheckCircle2, Clock, User, Sparkles, FileText } from 'lucide-react';
import { Button } from '../components/ui/Button';
import { useTheme } from '../contexts/ThemeContext';
interface FeatureCarouselScreenProps {
  onContinue: () => void;
}
export function FeatureCarouselScreen({
  onContinue
}: FeatureCarouselScreenProps) {
  const {
    isLight
  } = useTheme();
  const [currentIndex, setCurrentIndex] = useState(0);
  const features = [{
    id: 'calls',
    title: 'AI answers your calls',
    description: 'Never miss an opportunity. Vexu handles your missed calls with a natural-sounding AI voice.',
    renderVisual: () => {
      const missed = [{
        name: 'Sarah Connor',
        time: '9:41 AM'
      }, {
        name: 'Michael Chang',
        time: '8:12 AM'
      }, {
        name: 'Unknown number',
        time: 'Yesterday'
      }];
      return <div className="relative w-full h-52 flex flex-col items-center justify-center" data-id="element-2780" data-mp="3g1nbdqkpgnvb">
            {/* The one call Vexu AI handled — crisp, highlighted */}
            <motion.div initial={{
          opacity: 0,
          scale: 0.94,
          y: 12
        }} animate={{
          opacity: 1,
          scale: 1,
          y: 0
        }} transition={{
          delay: 0.15,
          type: 'spring',
          damping: 20
        }} className={`relative z-10 w-72 p-3.5 rounded-2xl shadow-xl border backdrop-blur-xl ${isLight ? 'bg-white border-vexu/30 shadow-vexu/10' : 'bg-[#1A1A1A]/95 border-vexu/30 shadow-black/50'}`} data-id="element-2781" data-mp="3l08311lw5z85">
              {/* soft accent glow */}
              <div className={`absolute -inset-px rounded-2xl pointer-events-none ${isLight ? 'ring-1 ring-vexu/20' : 'ring-1 ring-vexu/25'}`} data-id="element-2782" data-mp="3cvytfmnsoaix" />
              <div className="relative flex items-center gap-3" data-id="element-2783" data-mp="3lospgvrgnojh">
                <div className={`relative w-10 h-10 rounded-full flex items-center justify-center ${isLight ? 'bg-vexu/10 text-vexu-700' : 'bg-vexu/15 text-vexu'}`} data-id="element-2784" data-mp="3loam7f6dsa22">
                  <PhoneIncoming className="w-5 h-5" data-id="element-2785" data-mp="2txwc7u1hnhvo" />
                  <span className="absolute -top-0.5 -right-0.5 w-2.5 h-2.5 rounded-full bg-emerald-500 ring-2 ring-white dark:ring-[#1A1A1A]" data-id="element-2786" data-mp="2oz2utqj2z6q8" />
                </div>
                <div className="flex-1 min-w-0" data-id="element-2787" data-mp="32vy8xxe6vod5">
                  <div className={`text-sm font-bold truncate ${isLight ? 'text-slate-900' : 'text-white'}`} data-id="element-2788" data-mp="2xy6fgyxwbu6u">
                    Sarah Connor
                  </div>
                  <div className="flex items-center gap-1" data-id="element-2789" data-mp="d87bf0jz259i">
                    <Sparkles className={`w-3 h-3 ${isLight ? 'text-vexu-600' : 'text-vexu'}`} data-id="element-2790" data-mp="lq9bad6lobg2" />
                    <span className={`text-xs font-medium ${isLight ? 'text-vexu-700' : 'text-vexu'}`} data-id="element-2791" data-mp="pse3d7heh00l">
                      Vexu AI answered · took a message
                    </span>
                  </div>
                </div>
                <div className="flex items-end gap-0.5 h-5" data-id="element-2792" data-mp="14fz793m3n39k">
                  {[1, 2, 3, 4].map(i => <motion.div key={i} animate={{
                height: [6, 16, 6]
              }} transition={{
                repeat: Infinity,
                duration: 1,
                delay: i * 0.15
              }} className={`w-1 rounded-full ${isLight ? 'bg-vexu' : 'bg-vexu'}`} data-id="element-2793" data-mp="19fvc152meyq6" />)}
                </div>
              </div>
            </motion.div>

            {/* The faded missed-calls log — what you would have missed */}
            <div className="w-64 -mt-1" data-id="element-2794" data-mp="3vn9u03g1dhuc">
              {missed.map((m, i) => <motion.div key={m.name} initial={{
            opacity: 0,
            y: 8
          }} animate={{
            opacity: 0.55 - i * 0.13,
            y: 0
          }} transition={{
            delay: 0.35 + i * 0.12
          }} style={{
            transform: `scale(${0.97 - i * 0.03})`
          }} className={`mx-auto mt-2 px-3 py-2.5 rounded-xl flex items-center gap-3 ${isLight ? 'bg-slate-100' : 'bg-white/5'}`} data-id="element-2795" data-mp="3qo6pck3yxdmw" data-mpi={i}>
                  <div className={`p-1.5 rounded-full ${isLight ? 'bg-red-100 text-red-500' : 'bg-red-500/20 text-red-400'}`} data-id="element-2796" data-mp="160u0n7j6f8ic">
                    <PhoneMissed className="w-3.5 h-3.5" data-id="element-2797" data-mp="18kguulsangvy" />
                  </div>
                  <div className="flex-1 min-w-0" data-id="element-2798" data-mp="1dhgtwcqq7jdk">
                    <div className={`text-xs font-semibold truncate ${isLight ? 'text-slate-500' : 'text-zinc-400'}`} data-id="element-2799" data-mp="1dk3af1vlh97x">
                      {m.name}
                    </div>
                    <div className={`text-[10px] ${isLight ? 'text-slate-400' : 'text-zinc-500'}`} data-id="element-2800" data-mp="1rib1it5y9gb6">
                      Missed call
                    </div>
                  </div>
                  <span className={`text-[10px] ${isLight ? 'text-slate-400' : 'text-zinc-500'}`} data-id="element-2801" data-mp="217kx4j005h41">
                    {m.time}
                  </span>
                </motion.div>)}
            </div>
          </div>;
    }
  }, {
    id: 'calendar',
    title: 'Books your meetings',
    description: 'Vexu checks your availability and schedules appointments directly onto your calendar.',
    renderVisual: () => <div className="relative w-full h-52 flex items-center justify-center" data-id="element-2802" data-mp="b4kcfawzm514">
          <motion.div initial={{
        opacity: 0,
        y: 16,
        scale: 0.96
      }} animate={{
        opacity: 1,
        y: 0,
        scale: 1
      }} transition={{
        delay: 0.15,
        type: 'spring',
        damping: 22
      }} className={`w-72 rounded-[28px] shadow-xl border backdrop-blur-xl overflow-hidden ${isLight ? 'bg-white border-slate-200/80 shadow-slate-300/40' : 'bg-[#161616]/95 border-white/10 shadow-black/50'}`} data-id="element-2803" data-mp="ovpdmhg075dk">
            {/* Header */}
            <div className={`flex items-center justify-between px-5 pt-4 pb-3.5 border-b ${isLight ? 'border-slate-100' : 'border-white/[0.07]'}`} data-id="element-2804" data-mp="39mg34xtt9bng">
              <div data-id="element-2805" data-mp="2vg4lyzyehh5v">
                <div className={`text-[10px] font-semibold uppercase tracking-[0.12em] ${isLight ? 'text-slate-400' : 'text-zinc-500'}`} data-id="element-2806" data-mp="2vgxn9lelbegm">
                  Tomorrow
                </div>
                <div className={`text-sm font-bold ${isLight ? 'text-slate-900' : 'text-white'}`} data-id="element-2807" data-mp="2lpjt7t5er6uv">
                  Wed, June 24
                </div>
              </div>
              <div className={`w-9 h-9 rounded-2xl flex items-center justify-center ${isLight ? 'bg-purple-50 text-purple-600' : 'bg-purple-500/15 text-purple-400'}`} data-id="element-2808" data-mp="2bqki83x66z4p">
                <CalendarDays className="w-[18px] h-[18px]" data-id="element-2809" data-mp="22t1swnchtyn0" />
              </div>
            </div>

            {/* Agenda */}
            <div className="px-5 py-4 space-y-2.5" data-id="element-2810" data-mp="27s4ws4gqlyte">
              {/* Faded existing slot */}
              <div className="flex items-center gap-3 opacity-40" data-id="element-2811" data-mp="xzv06930107">
                <span className={`text-[11px] font-semibold tabular-nums w-11 shrink-0 ${isLight ? 'text-slate-400' : 'text-zinc-500'}`} data-id="element-2812" data-mp="5yf58oy2gsdy">
                  10:00
                </span>
                <div className={`flex-1 h-7 rounded-lg ${isLight ? 'bg-slate-100' : 'bg-white/[0.06]'}`} data-id="element-2813" data-mp="19mcnvzl7zmwu" />
              </div>

              {/* The AI-booked meeting — crisp, detailed */}
              <div className="flex items-stretch gap-3" data-id="element-2814" data-mp="vsv2sacvy24p">
                <span className={`text-[11px] font-bold tabular-nums w-11 shrink-0 pt-2 ${isLight ? 'text-purple-600' : 'text-purple-400'}`} data-id="element-2815" data-mp="vufzo6mo9x5q">
                  2:30
                </span>
                <motion.div initial={{
              opacity: 0,
              x: -14
            }} animate={{
              opacity: 1,
              x: 0
            }} transition={{
              delay: 0.5,
              type: 'spring',
              damping: 20
            }} className={`relative flex-1 rounded-2xl p-3 border ${isLight ? 'bg-purple-50/80 border-purple-200' : 'bg-purple-500/10 border-purple-500/25'}`} data-id="element-2816" data-mp="lwie5qw76egt">
                  {/* accent bar */}
                  <span className={`absolute left-0 top-3 bottom-3 w-1 rounded-full ${isLight ? 'bg-purple-500' : 'bg-purple-400'}`} data-id="element-2817" data-mp="bqii16x2amb8" />
                  <div className="flex items-center justify-between mb-2 pl-1.5" data-id="element-2818" data-mp="3b7yn11vd4tus">
                    <span className={`text-xs font-bold ${isLight ? 'text-purple-900' : 'text-purple-50'}`} data-id="element-2819" data-mp="3g5i7wkmkg4vp">
                      Call with Sarah
                    </span>
                    <div className={`w-5 h-5 rounded-full flex items-center justify-center ${isLight ? 'bg-purple-500 text-white' : 'bg-purple-400 text-purple-950'}`} data-id="element-2820" data-mp="3p3k7cvqnipgv">
                      <CheckCircle2 className="w-3.5 h-3.5" data-id="element-2821" data-mp="14h8tpnmvl6u1" />
                    </div>
                  </div>
                  <div className="flex items-center justify-between pl-1.5" data-id="element-2822" data-mp="1hags9mw2wjam">
                    <div className="flex items-center gap-1.5" data-id="element-2823" data-mp="1h8nme9tai33o">
                      <div className={`w-5 h-5 rounded-full flex items-center justify-center text-[9px] font-bold ${isLight ? 'bg-purple-200 text-purple-700' : 'bg-purple-500/30 text-purple-200'}`} data-id="element-2824" data-mp="1m2rpuj3cjt00">
                        SC
                      </div>
                      <span className={`text-[10px] ${isLight ? 'text-purple-700/80' : 'text-purple-200/70'}`} data-id="element-2825" data-mp="ycnpqlpdabdr">
                        30 min
                      </span>
                    </div>
                    <div className={`inline-flex items-center gap-1 px-1.5 py-0.5 rounded-full ${isLight ? 'bg-white text-purple-600' : 'bg-purple-500/20 text-purple-300'}`} data-id="element-2826" data-mp="2eq4fx38puiy2">
                      <Sparkles className="w-2.5 h-2.5" data-id="element-2827" data-mp="74vx0rijdthx" />
                      <span className="text-[9px] font-bold tracking-wide" data-id="element-2828" data-mp="76gqsex118eq">
                        Booked by Vexu
                      </span>
                    </div>
                  </div>
                </motion.div>
              </div>

              {/* Faded existing slot */}
              <div className="flex items-center gap-3 opacity-30" data-id="element-2829" data-mp="3phu0h8ejf4gu">
                <span className={`text-[11px] font-semibold tabular-nums w-11 shrink-0 ${isLight ? 'text-slate-400' : 'text-zinc-500'}`} data-id="element-2830" data-mp="3kjirt0zzb007">
                  4:00
                </span>
                <div className={`flex-1 h-7 rounded-lg ${isLight ? 'bg-slate-100' : 'bg-white/[0.06]'}`} data-id="element-2831" data-mp="3bjdjmomynxkz" />
              </div>
            </div>
          </motion.div>
        </div>
  }, {
    id: 'summaries',
    title: 'Instant summaries',
    description: 'Get concise transcripts and actionable summaries immediately after every interaction.',
    renderVisual: () => <div className="relative w-full h-52 flex items-center justify-center" data-id="element-2832" data-mp="1nun24374xqep">
          <motion.div initial={{
        opacity: 0,
        y: 16,
        scale: 0.96
      }} animate={{
        opacity: 1,
        y: 0,
        scale: 1
      }} transition={{
        delay: 0.15,
        type: 'spring',
        damping: 22
      }} className={`w-72 rounded-[28px] shadow-xl border backdrop-blur-xl overflow-hidden ${isLight ? 'bg-white border-slate-200/80 shadow-slate-300/40' : 'bg-[#161616]/95 border-white/10 shadow-black/50'}`} data-id="element-2833" data-mp="1sor74gwubod5">
            {/* Header */}
            <div className={`flex items-center justify-between px-5 pt-4 pb-3.5 border-b ${isLight ? 'border-slate-100' : 'border-white/[0.07]'}`} data-id="element-2834" data-mp="22ngqyf8ariw1">
              <div className="flex items-center gap-2.5" data-id="element-2835" data-mp="r81rn4sm8exr">
                <div className={`w-9 h-9 rounded-2xl flex items-center justify-center ${isLight ? 'bg-orange-50 text-orange-600' : 'bg-orange-500/15 text-orange-400'}`} data-id="element-2836" data-mp="jmg2n51tqhyt">
                  <FileText className="w-[18px] h-[18px]" data-id="element-2837" data-mp="snw6sv21pnul" />
                </div>
                <div data-id="element-2838" data-mp="not0766f39ki">
                  <div className={`text-sm font-bold leading-tight ${isLight ? 'text-slate-900' : 'text-white'}`} data-id="element-2839" data-mp="npm9adsumb5t">
                    Call Summary
                  </div>
                  <div className={`text-[10px] ${isLight ? 'text-slate-400' : 'text-zinc-500'}`} data-id="element-2840" data-mp="3s42hmbwm77cs">
                    Michael Chang · 4m 12s
                  </div>
                </div>
              </div>
              <div className={`inline-flex items-center gap-1 px-2 py-1 rounded-full ${isLight ? 'bg-orange-50 text-orange-600' : 'bg-orange-500/15 text-orange-300'}`} data-id="element-2841" data-mp="4w8nozb0zh2v">
                <Sparkles className="w-2.5 h-2.5" data-id="element-2842" data-mp="3m1irems5k82w" />
                <span className="text-[9px] font-bold tracking-wide" data-id="element-2843" data-mp="3m2ttmi5y97rw">AI</span>
              </div>
            </div>

            <div className="px-5 py-4 space-y-3.5" data-id="element-2844" data-mp="3r13th62mcwju">
              {/* Condensed summary sentence */}
              <motion.p initial={{
            opacity: 0
          }} animate={{
            opacity: 1
          }} transition={{
            delay: 0.45
          }} className={`text-[11px] leading-relaxed ${isLight ? 'text-slate-600' : 'text-zinc-400'}`} data-id="element-2845" data-mp="3vydnz04v7yej">
                Caller asked to{' '}
                <span className={`font-semibold ${isLight ? 'text-slate-900' : 'text-zinc-200'}`} data-id="element-2846" data-mp="iipkgstvqhhp">
                  reschedule the demo
                </span>{' '}
                and requested updated pricing.
              </motion.p>

              {/* Key points */}
              <div className="space-y-2" data-id="element-2847" data-mp="2pd2zwl5hfon5">
                {[{
              label: 'Move demo to Tuesday 2 PM',
              delay: 0.62
            }, {
              label: 'Send updated pricing sheet',
              delay: 0.74
            }].map(item => <motion.div key={item.label} initial={{
              opacity: 0,
              x: -10
            }} animate={{
              opacity: 1,
              x: 0
            }} transition={{
              delay: item.delay,
              type: 'spring',
              damping: 20
            }} className="flex items-center gap-2.5" data-id="element-2848" data-mp="sq0e4uvval5y">
                    <CheckCircle2 className={`w-4 h-4 shrink-0 ${isLight ? 'text-orange-500' : 'text-orange-400'}`} data-id="element-2849" data-mp="1n4ty44dyn" />
                    <span className={`text-xs font-medium ${isLight ? 'text-slate-700' : 'text-zinc-300'}`} data-id="element-2850" data-mp="1hqlnu3616xg">
                      {item.label}
                    </span>
                  </motion.div>)}
              </div>

              {/* Highlighted action item */}
              <motion.div initial={{
            opacity: 0,
            y: 8
          }} animate={{
            opacity: 1,
            y: 0
          }} transition={{
            delay: 0.9,
            type: 'spring',
            damping: 20
          }} className={`relative flex items-center gap-2.5 rounded-2xl p-3 border ${isLight ? 'bg-orange-50/80 border-orange-200' : 'bg-orange-500/10 border-orange-500/25'}`} data-id="element-2851" data-mp="3tmufxhdomt9z">
                <span className={`absolute left-0 top-3 bottom-3 w-1 rounded-full ${isLight ? 'bg-orange-500' : 'bg-orange-400'}`} data-id="element-2852" data-mp="30vswh42yibkn" />
                <div className={`w-7 h-7 rounded-xl flex items-center justify-center shrink-0 ml-1 ${isLight ? 'bg-orange-500 text-white' : 'bg-orange-400 text-orange-950'}`} data-id="element-2853" data-mp="2cc90spzwma35">
                  <Clock className="w-4 h-4" data-id="element-2854" data-mp="2m8lqzdw8q5t5" />
                </div>
                <div className="min-w-0" data-id="element-2855" data-mp="3094zem4mbdvu">
                  <div className={`text-[9px] font-bold uppercase tracking-wider ${isLight ? 'text-orange-500' : 'text-orange-400'}`} data-id="element-2856" data-mp="3083iw24u1z6q">
                    Follow-up
                  </div>
                  <div className={`text-xs font-semibold truncate ${isLight ? 'text-orange-900' : 'text-orange-50'}`} data-id="element-2857" data-mp="3a1aljf86v134">
                    Send proposal by Friday
                  </div>
                </div>
              </motion.div>
            </div>
          </motion.div>
        </div>
  }];
  const handleNext = () => {
    if (currentIndex < features.length - 1) {
      setCurrentIndex(prev => prev + 1);
    } else {
      onContinue();
    }
  };
  return <div className="h-full flex flex-col p-6 relative overflow-hidden" data-id="element-2858" data-mp="u8s9brhn1qyx">
      {/* Background orbs */}
      <div className={`absolute top-20 -right-20 w-60 h-60 rounded-full blur-3xl pointer-events-none ${isLight ? 'bg-vexu/15' : 'bg-vexu/10'}`} data-id="element-2859" data-mp="137nh380bz94c" />
      <div className={`absolute bottom-40 -left-20 w-60 h-60 rounded-full blur-3xl pointer-events-none ${isLight ? 'bg-blue-500/10' : 'bg-blue-500/5'}`} data-id="element-2860" data-mp="6iy1xpg38um2" />

      {/* Progress dots */}
      <div className="flex justify-center gap-2 pt-8 relative z-10" data-id="element-2861" data-mp="fgi4d1poswy9">
        {features.map((_, i) => <div key={i} className={`h-1.5 rounded-full transition-all duration-300 ${i === currentIndex ? `w-6 ${isLight ? 'bg-vexu' : 'bg-vexu'}` : `w-1.5 ${isLight ? 'bg-slate-200' : 'bg-white/20'}`}`} data-id="element-2862" data-mp="ka2s80xkgajq" data-mpi={i} />)}
      </div>

      <div className="flex-1 flex flex-col items-center justify-center relative z-10 w-full" data-id="element-2863" data-mp="3gelqlalfmeqg">
        <AnimatePresence mode="wait" data-id="element-2864" data-mp="3beqxd0e6pd28">
          <motion.div key={currentIndex} initial={{
          opacity: 0,
          x: 20
        }} animate={{
          opacity: 1,
          x: 0
        }} exit={{
          opacity: 0,
          x: -20
        }} transition={{
          duration: 0.3
        }} className="w-full flex flex-col items-center text-center" data-id="element-2865" data-mp="3bfsffotmaztg">
            {/* Custom Visual */}
            <div className="w-full mb-8" data-id="element-2866" data-mp="2nq7rssmiredu">
              {features[currentIndex].renderVisual()}
            </div>

            <h2 className={`text-3xl font-bold mb-4 tracking-tight ${isLight ? 'text-slate-900' : 'text-white'}`} data-id="element-2867" data-mp="2jpxm7h2n2pzi">
              {features[currentIndex].title}
            </h2>

            <p className={`text-lg max-w-[280px] leading-relaxed ${isLight ? 'text-slate-500' : 'text-zinc-400'}`} data-id="element-2868" data-mp="29rzzwz4cb39j">
              {features[currentIndex].description}
            </p>
          </motion.div>
        </AnimatePresence>
      </div>

      <div className="w-full pb-8 relative z-10" data-id="element-2869" data-mp="1yi2zw6d486lb">
        <Button fullWidth size="lg" onClick={handleNext} icon={currentIndex === features.length - 1 ? undefined : <ArrowRight className="w-5 h-5" data-id="element-2871" data-mp="1pksp0m47mjgx" />} className="flex-row-reverse" data-id="element-2870" data-mp="1tkb8ddgdumhn">
          {currentIndex === features.length - 1 ? 'Continue' : 'Next'}
        </Button>
        <button onClick={onContinue} className={`w-full text-center text-sm font-medium mt-4 transition-colors ${isLight ? 'text-slate-400 hover:text-slate-600' : 'text-zinc-500 hover:text-zinc-300'}`} data-id="element-2872" data-mp="1fqse23m8yk0a">
          Skip
        </button>
      </div>
    </div>;
}